import { Component, ErrorHandler, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService } from '../../services/chat.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { v4 as uuidv4 } from 'uuid';
import { LoaderComponent } from '../loader/loader.component';
import { AnswerButtonComponent } from '../answer-button/answer-button.component';
import { Chat, ImageOption, multiselectOption, Options } from '../../models/chat';
import { additionalInfo, Messages, Product, ProductInfo, ResponseData } from '../../models/response';
import { ProductsComponent } from '../products/products.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoadingInterceptor } from '../../services/loading.interceptor';
import { RollbarConfigInjector, RollbarErrorHandler, RollbarService, rollbarConfig, rollbarFactory } from '../../services/rollbar.service';
import { LoaderService } from '../../services/loading.service';
import { MultiselectButtonComponent } from '../multiselect-button/multiselect-button.component';
import { FeedbackDetailComponent } from '../feedback-detail/feedback-detail.component';
import { StateValue } from '../../models/state';
import { storeOption } from '../../models/store';
import { ProductMessage } from '../../models/product-qa-chat';
import { displayObject } from '../../models/display';
import { ProductQaComponent } from '../product-qa/product-qa.component';
import { AnswerImageButtonComponent } from '../answer-image-button/answer-image-button.component';
import { ConfigService } from '../../services/config.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { MessageService } from '../../services/message.service';
import { FeedbackMainComponent } from '../feedback/feedback.component';
import { ProductsService } from '../../services/products.service';
import { AnalyticsService } from '../../services/analytics.service';
import { searchProduct } from '../../models/search-product';
import { storedConfig } from '../../models/params';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    MatInputModule, 
    MatFormFieldModule,
    FormsModule, 
    MatButtonModule, 
    MatGridListModule,
    LoaderComponent,
    AnswerButtonComponent, 
    CommonModule,
    ProductsComponent,
    MultiselectButtonComponent,
    FeedbackDetailComponent,
    ProductQaComponent,
    AnswerImageButtonComponent,
    FeedbackMainComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    LoaderService,
    { provide: RollbarConfigInjector, useValue: rollbarConfig },
    { provide: RollbarService, useFactory: rollbarFactory },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, OnDestroy {

  // messages
  messages:Chat[] = [];
  // user input for messages
  userInput = '';
  // unique conversation id
  uniqueConversationId = '';
  // loader flag
  loading = false;
  // answers
  answers:string[] = [];
  // boolean to initiate new chat
  initiateNewChat = false;
  // feedback detail flag
  feedbackDetail = false;
  // multiselect options
  multiselectOptions:string[] = [];
  // state name
  stateCode:string | undefined = '';
  // store name
  storeType:string | undefined = '';
  // product q and a
  productMessages:ProductMessage[] = [];
  // show product panel
  showProductPanel = false;
  // mobile view
  mobileView = false;
  // selected product object
  selectedProduct:Product = {};
  // view object for displaying content
  displayValue:displayObject = { type: 'desktop', showScotty: true, showproduct: true };
  // original window width
  windowWidth = 0;
  // chat type
  type = '';
  // selected products
  selectedProducts:Product[] = [];
  // maxCount
  maxCount = 0;
  // compareStatus flag
  compareStatus = true;
  // message index to check the current message index
  messageIndex = 0;
  // product ids
  productIds:string[] = [];
  // product additional information
  productAdditionalInfo: additionalInfo[] = [];
  // original stores
  originalStores: string[] = ['HD','LO','WM'];

  constructor(private sanitizer: DomSanitizer, private chatService:ChatService, private snackBarService:SnackBarService, private configService:ConfigService, private messageService:MessageService, private productService:ProductsService, private analyticsService: AnalyticsService) {
    this.chatService.chatSubscription = this.configService.storeOptions.subscribe((storeOptionValue:storeOption) => {
      if(Object.keys(storeOptionValue).length > 0){
        const state = this.configService.states.filter((state:StateValue) => state.name === storeOptionValue.stateName);
        const stateCode = state.length > 0 ? state[0].code : storeOptionValue.stateName;
        this.initiateNewChat = false;
        this.storeType = storeOptionValue.storeName;
        this.stateCode = stateCode; 
        this.messages = [];
        this.initializeConversation();
      }
    });
  }

  ngOnInit(): void {
    // To check if the product panel is closed in mobile view
    this.chatService.showPanel.subscribe((flag:boolean) => {
      if(flag === false){
        this.showProductPanel = false;
        this.selectedProduct = {};
        this.setView();
      }
    });
    // Check if the chat is deleted.
    this.chatService.deleteSubscription = this.chatService.deleteFlag.subscribe((flag:boolean) => {
      this.initiateNewChat = flag;
      if(!this.initiateNewChat){
        this.messages = [];
        this.productAdditionalInfo = [];
        this.chatService.currentStateOfChat = { sessionId: '', chat: []};
        this.initializeConversation();
      }
    });

    this.chatService.compareSubscription =  this.chatService.compareSelectFlag.subscribe((flag:boolean) => {
      if(flag){
        this.compareProducts();
      }
    });

    // set original windoWdith
    this.windowWidth = window.innerWidth;
    // To initialize view
    this.setView();
  }

  ngOnDestroy() {
    this.chatService.deleteSubscription.unsubscribe();
    this.chatService.chatSubscription.unsubscribe();
    this.chatService.setCompareSelectFlag(false);
    this.chatService.compareSubscription.unsubscribe();
  }
  
  sendMessage() {
    // if messages length is zero that means a new conversation is to be started 
    if(this.messages.length === 0){
      this.uniqueConversationId = uuidv4();
    }
    this.sendUserResponse();
  }

  initializeConversation(){
    const name = this.chatService.userInfo.name + ' ' + this.chatService.userInfo.family_name;
    this.resetCompareStatus();
    if(this.chatService.currentStateOfChat.chat.length > 0){
      this.uniqueConversationId = this.chatService.currentStateOfChat.sessionId;
      this.messages = this.chatService.currentStateOfChat.chat;
      this.chatService.setCurrentRouteInfo('');
      this.messages = this.productService.resetProducts(this.messages);
    } else {
      this.uniqueConversationId = uuidv4();
      // Initialize conversation with true flag
      this.getAgentResponse("Hello I am " + name, true);
      this.chatService.setDeleteFlag(true);
    }
  }

  sendOptions(event:string, index:number){
    const selectedAnswer = event;
    this.userInput = selectedAnswer;
    this.messages[index].answers = this.messages[index].answers?.filter((answer) => answer === selectedAnswer);
    this.messages[index].answerSelected = true;
    this.sendUserResponse();
  }

  sendUserResponse(){
    if (this.userInput.trim()) {
      // removing the special characters from text
      this.userInput = this.userInput.replace(/[^a-zA-Z0-9\s/,]/g, '');
      // trim the text for any spaces
      this.userInput = this.userInput.trim();
      if(this.userInput.length === 0){
        this.snackBarService.openSnackBar("Please do not enter special characters or empty string", "Dismiss", 5000);
        return;
      }
      const answerSelectedFlag = this.messages[this.messages.length - 1].answerSelected;
      if(answerSelectedFlag === false){
        this.messages[this.messages.length - 1].answers = [];
        this.messages[this.messages.length - 1].multiselectOptions = [];
        this.messages[this.messages.length - 1].imageOptions = [];
      };
      this.messages.push({ sender: 'user', text: this.userInput , responseId: '0', feedback: 'None', answerSelected: false, messageType: 'user_message', selectedMultiselectOptions: [], multiselectOptions: [], products: []});
      this.getAgentResponse(this.userInput, false);
      this.userInput = '';
    }
  }
  
  getAgentResponse(userInput:string, init:boolean) {
    this.loading = true;
    this.chatService.askScotty(userInput,this.uniqueConversationId, this.storeType, this.stateCode, init, this.chatService.userInfo).subscribe({
      next: (res:ResponseData) => {
        const messages = res.messages;
        let new_session = false;
        this.productIds = [];
        messages.forEach((message:Messages) => {
          if(message){
            const answers = message.question_answers;
            new_session = message.new_session ? true : false;
            const obj = this.messageService.transformMessage(answers, message.question_type, message.message_type, res.products, message.product_ids);
            this.messages.push({ messageType: message.message_type, responseId: res.responseId, text: message.display_text, ...obj});
            if(message.message_type === 'recommendations'){
              this.productIds = [...this.messageService.getProductIds(obj.products)];
            }
          }
        });
        if(this.productIds.length > 0){
          this.checkParams(this.productIds, this.messages.length - 2);
        }
        this.chatService.currentStateOfChat = { sessionId: this.uniqueConversationId, chat: this.messages };
        this.loading = false;
        if(new_session){
          // to initialize a new conversation if new_session is there in message keys
          this.chatService.currentStateOfChat = { sessionId: '', chat: [] };
          this.initializeConversation();
        }
      },
      error: (error) => {
        this.loading = false;
        this.snackBarService.showErrorMessage(error.error.statusCode);
      }
    });
  }

  checkParams(productIds: string[], messageIndex: number){
    const storeObject: storedConfig = JSON.parse(localStorage.getItem('config') || "{}");
    const originalStoreId = storeObject?.originalStoreId ? storeObject.originalStoreId : '';
    if(storeObject.stateCode && storeObject.storeId && this.originalStores.includes(originalStoreId.toUpperCase())){
      this.getProductAvailability(productIds, storeObject.originalStoreId, storeObject.storeId, messageIndex);
    }
  }

  getProductAvailability(productIds: string[], store:string, storeId:string, messageIndex:number){
    this.productService.getStoreInventory(productIds, store, storeId).subscribe({
      next: (response: additionalInfo[]) => {
        this.productAdditionalInfo = [...this.productAdditionalInfo, ...response];
        this.messages[messageIndex].products = this.messages[messageIndex].products.map((product:searchProduct) => {
          const availableProductData = this.productAdditionalInfo.find((value) => value.id === product.product.id);
          product.product = {
            ...product.product,
            Stock_availability: availableProductData?.inventory_available
          };
          return product;
        });
      },
      error: (error) => {
        this.snackBarService.showErrorMessage(error.error.statusCode);
      }
    });
  }

  getSafeHTML(html:string):SafeHtml{
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
  
  checkOption(event:multiselectOption){
    this.messages[event.index].multiselectOptions = this.messages[event.index].multiselectOptions.map((value:Options) => {
      if(value.name === event.name){
        return { name: value.name, flag: !event.flag}
      }
      return value;
    });
    if(this.messages[event.index].selectedMultiselectOptions.includes(event.name)){
      const i = this.messages[event.index].selectedMultiselectOptions.indexOf(event.name);
      this.messages[event.index].selectedMultiselectOptions.splice(i,1);
    } else {
      this.messages[event.index].selectedMultiselectOptions.push(event.name);
    }
  }

  submitOptions(index:number){
    this.messages[index].multiselectOptions = this.messages[index].multiselectOptions.filter((value:Options) => value.flag);
    const selectedValuesCombined: string[] = [];
    this.messages[index].multiselectOptions.forEach((value:Options) => {
      selectedValuesCombined.push(value.name);
    });
    this.messages[index].answerSelected = true;
    this.userInput = selectedValuesCombined.join(', ');
    this.sendUserResponse();
  }

  expandPanel(event:ProductInfo){
    this.productMessages = [];
    this.showProductPanel = event.flag;
    this.selectedProduct = event.product;
    this.selectedProducts = [];
    this.type = 'qna';
    // if the display is mobile
    if(this.mobileView){
      this.displayValue = { type: 'mobile', showScotty: false, showproduct: true };
      this.chatService.currentBotValue = 'qna';
      this.chatService.setCheckFlag(true);
      this.chatService.setPanelInformation(true);
    }
  }

  compareProducts() {
    // Always close the filters if right side panel is opening
    this.selectedProduct = {};
    this.productMessages = [];
    this.selectedProducts = this.productService.getSelectedProducts(this.messages);
    this.type = 'comparison';
    this.showProductPanel = true;
    // Track the comparison event in Firebase
    this.analyticsService.trackEvent('product_compare_view', {
      ccompared_products: (this.selectedProducts || []).join(','),
      total_products: this.selectedProducts.length,
      view_type: this.mobileView ? 'mobile' : 'desktop',
      timestamp: new Date().toISOString(),
    });
    // if the display is mobile
    if (this.mobileView) {
      this.displayValue = { type: 'mobile', showScotty: false, showproduct: true };
      this.chatService.currentBotValue = 'compare';
      this.chatService.setCheckFlag(true);
      this.chatService.setPanelInformation(true);
    }
  }

  closePanel(event:boolean){
    this.showProductPanel = event;
    this.selectedProduct = {};
    this.selectedProducts = [];
    this.type = '';
    this.chatService.currentBotValue = '';
  }

  @HostListener('window:resize', ['$event.target.innerWidth'])
  onResize(width: number) {
    // if the resized witdth not equal to original windowWidth
    if(width !== this.windowWidth){
      this.setView();
    }
  }

  setView(){
    if(window.innerWidth >= 768){
      this.mobileView = false;
      const value = { type: "desktop", showScotty:true, showproduct: true };
      this.displayValue = value;
    } else {
      this.mobileView = true;
      const value = { type: "mobile", showScotty:true, showproduct: false };
      this.displayValue = value;
    }

    // To check if the product qna panel is still visible in the view
    if(this.chatService.panelCheckFlag){
      this.chatService.setCheckFlag(false);
      this.chatService.setPanelInformation(false);
    }
  }

  sendAnswer(event:string, index:number){
    const selectedAnswer = event;
    this.userInput = selectedAnswer;
    this.messages[index].imageOptions = this.messages[index].imageOptions?.filter((imageOption:ImageOption) => imageOption.name === selectedAnswer);
    this.messages[index].answerSelected = true;
    this.sendUserResponse();
  }

  checkSelectOption(index: number, messageIndex:number) {
    if(messageIndex !== this.messageIndex){
      this.messageIndex = messageIndex;
      this.messages = this.productService.resetProducts(this.messages);
    }
    this.maxCount = 0;
    this.messages[messageIndex].products[index]['selected'] = !this.messages[messageIndex].products[index]['selected'];
    const selectedProducts = this.messages[messageIndex].products.filter((data) => data.selected);
    this.compareStatus = selectedProducts.length > 1 ? false : true;
    this.maxCount = selectedProducts.length;
    // set compare status
    this.chatService.setCompareStatus(this.compareStatus);
    // show a message if comparison limit of three is reached
    if(this.maxCount === 3){
      this.snackBarService.openSnackBar('Max three products are allowed to be selected for comparison', "Dismiss", 5000);
    }
  }

  resetCompareStatus(){
    // reset compare status variables
    this.chatService.setCompareStatus(true);
    this.maxCount = 0;
    this.compareStatus = true;
  }

}
